import { useEffect, useRef, useState, useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { whoami } from "../../lib/auth";
import type { SessionUser } from "../../types/auth";
import {
  getCashDrawerDaily,
  getPendingBankDeposit,
  getCashDrawerMonthly,
  submitCashDrawerCount,
  type CashDrawerDailyResponse,
  type PendingBankDepositResponse,
  type CashDrawerMonthlyResponse,
  type CashDrawerShift,
  type CashDrawerBank,
  type CashDrawerCountRow,
} from "../../lib/api";
import { computeClosingSplit } from "../../lib/cashSplit";
import { cashBusinessDate } from "../../lib/cashBusinessDate";
import { squareCloseCheck, squareCloseInput, type CheckTone } from "../../lib/cashSquareCheck";
import { drawerStaff, orderByRegister, registerCashSales, splitHeldOverCash, type HeldOverDay } from "../../lib/depositVerify";
import DepositBagsSection from "../../components/cash/DepositBagsSection";
import BarcodeScannerModal from "../../components/common/BarcodeScannerModal";
import BankRunPanel from "../../components/cash/BankRunPanel";
import BankRunHistory from "../../components/cash/BankRunHistory";
import OnShiftPanel from "../../components/cash/OnShiftPanel";
import DenominationCounter from "../../components/cash/DenominationCounter";
import { DENOMS, denomTotalCents } from "../../lib/denominations";

const CHECK_ICON: Record<CheckTone, string> = { ok: "✓", bad: "⚠", pending: "⏳", info: "•" };
const CHECK_COLOR: Record<CheckTone, string> = {
  ok: "var(--green, #27ae60)",
  bad: "var(--red, #E05040)",
  pending: "var(--muted, #8A7A66)",
  info: "var(--text, #F0E6D4)",
};

function getCleanDeviceName(rawName: string | undefined | null): string {
  if (!rawName) return "Register";
  const lower = rawName.toLowerCase().trim();
  if (lower.includes("lounge") || lower.includes("sr pos") || lower === "main bar") return "Lounge POS";
  if (lower.includes("library")) return "Library POS";
  if (lower.includes("parlor") || lower.includes("marinello") || lower.includes("retail")) return "Parlor POS";
  return rawName;
}

// Register cards (and the deposit bag cards, which follow them) read left to
// right in the room's order. Unlisted registers keep their order after these.
const REGISTER_ORDER = ["Lounge POS", "Parlor POS", "Library POS"];
function registerRank(cleanName: string): number {
  const i = REGISTER_ORDER.indexOf(cleanName);
  return i === -1 ? REGISTER_ORDER.length : i;
}

function getDefaultDailyData(dateStr: string): CashDrawerDailyResponse {
  return {
    summary: {
      business_date: dateStr,
      total_cash_sales_cents: 0,
      total_tips_cents: 0,
      total_paid_in_cents: 0,
      total_paid_out_cents: 0,
      expected_closing_cents: 75000,
      total_closing_counted_cents: 75000,
      total_deposit_cents: 0,
      total_variance_cents: 0,
      has_discrepancy: false,
    },
    shifts: [],
    banks: [
      {
        device_id: "E4193A6C-CB05-4F56-BC81-DEDD54766368",
        device_name: "Lounge POS",
        standard_bank_cents: 30000,
        bank_breakdown: { "1": 300, "5": 40, "10": 50, "25": 40, "100": 40, "500": 12, "1000": 8, "2000": 5 },
        low_thresholds: null,
      },
      {
        device_id: "322CS134A4000885",
        device_name: "Parlor POS",
        standard_bank_cents: 15000,
        bank_breakdown: { "1": 100, "5": 20, "10": 30, "25": 20, "100": 30, "500": 6, "1000": 4, "2000": 2 },
        low_thresholds: { "1": 40, "5": 20, "10": 20, "25": 40, "100": 20 },
      },
      {
        device_id: "9995C819-BEF6-4C8A-ABDF-D4A7FBF18D2E",
        device_name: "Library POS",
        standard_bank_cents: 30000,
        bank_breakdown: { "1": 300, "5": 40, "10": 50, "25": 40, "100": 40, "500": 16, "1000": 10, "2000": 3 },
        low_thresholds: null,
      },
    ],
    counts: [],
    events: [],
  };
}

function getDefaultMonthlyData(year: number, month: number): CashDrawerMonthlyResponse {
  return {
    year,
    month,
    summary: {
      total_sales_cents: 0,
      total_deposits_cents: 0,
      total_variance_cents: 0,
      days_count: 0,
    },
    days: [],
  };
}

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window === "undefined" ? false : window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

export interface ChangeSwapAssessment {
  needsSwap: boolean;
  largeBills: Array<{ label: string; count: number; cents: number }>;
  lowDenoms: Array<{ label: string; count: number; minNeeded: number }>;
  summary: string;
  recommendation: string;
}

export function detectDrawerChangeSwapNeeded(
  _bank: CashDrawerBank,
  count?: CashDrawerCountRow
): ChangeSwapAssessment {
  if (!count) {
    return { needsSwap: false, largeBills: [], lowDenoms: [], summary: "", recommendation: "" };
  }

  const breakdown = count.retained_breakdown || count.denomination_breakdown || {};
  const c100 = breakdown["10000"] || 0;
  const c50 = breakdown["5000"] || 0;
  const c20 = breakdown["2000"] || 0;
  const c5 = breakdown["500"] || 0;
  const c1 = breakdown["100"] || 0;
  const c25 = breakdown["25"] || 0;

  const largeBills: Array<{ label: string; count: number; cents: number }> = [];
  if (c100 > 0) largeBills.push({ label: "$100", count: c100, cents: 10000 });
  if (c50 > 0) largeBills.push({ label: "$50", count: c50, cents: 5000 });

  const lowDenoms: Array<{ label: string; count: number; minNeeded: number }> = [];
  if (c1 < 15) lowDenoms.push({ label: "$1 bills", count: c1, minNeeded: 30 });
  if (c5 < 4) lowDenoms.push({ label: "$5 bills", count: c5, minNeeded: 8 });
  if (c25 < 15) lowDenoms.push({ label: "Quarters", count: c25, minNeeded: 30 });

  const hasLargeBills = largeBills.length > 0;
  const isCriticallyLow = lowDenoms.length > 0 && c20 > 4;

  const needsSwap = hasLargeBills || isCriticallyLow;

  let summary = "";
  if (hasLargeBills) {
    summary = `Float holds ${largeBills.map((b) => `${b.count}x ${b.label}`).join(" & ")}`;
  } else if (isCriticallyLow) {
    summary = `Low change float (${lowDenoms.map((d) => `${d.label}: ${d.count}`).join(", ")})`;
  }

  let recommendation = "";
  if (hasLargeBills) {
    const totalLargeVal = largeBills.reduce((acc, b) => acc + b.count * b.cents, 0);
    recommendation = `Opening Lead: Exchange ${largeBills.map((b) => `${b.count}x ${b.label}`).join(" & ")} for $${(totalLargeVal / 100).toFixed(0)} in small bills ($5s, $1s, coin rolls) at the safe.`;
  } else if (isCriticallyLow) {
    recommendation = `Opening Lead: Swap 1-2 $20 bills for $5s, $1s, and coin rolls from the safe change fund.`;
  }

  return {
    needsSwap,
    largeBills,
    lowDenoms,
    summary,
    recommendation,
  };
}

export default function AdminCashManagement() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [currentUser, setCurrentUser] = useState<SessionUser | null>(null);
  const isOwnerOrManager = (currentUser?.bar_roles || []).some(
    (r) => r === "bar_owner" || r === "bar_manager" || r === "bar_lead"
  );
  // Deposit bag verification is management only (no leads): verify_drawer_deposit
  // refuses anyone without owner/manager, and re-open is owner only.
  const canVerifyBags = (currentUser?.bar_roles || []).some((r) => r === "bar_owner" || r === "bar_manager");
  const canReopenBags = (currentUser?.bar_roles || []).includes("bar_owner");
  const [scanBagOpen, setScanBagOpen] = useState(false);

  useEffect(() => {
    whoami().then(setCurrentUser);
  }, []);

  const [tab, setTab] = useState<"daily" | "monthly">("daily");
  const [currentDate, setCurrentDate] = useState<string>(cashBusinessDate());
  // Counts can only be recorded on the day the server will file them under.
  const bizToday = cashBusinessDate();
  const isBizToday = currentDate === bizToday;
  const [monthYear, setMonthYear] = useState<{ year: number; month: number }>({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });

  // Daily State — initialized immediately with default registers so there is NEVER a blank or frozen loading screen
  const [dailyData, setDailyData] = useState<CashDrawerDailyResponse>(() => getDefaultDailyData(cashBusinessDate()));
  const [pendingBank, setPendingBank] = useState<PendingBankDepositResponse | null>(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  // The date whose counts actually loaded. Count buttons wait for it: an empty
  // counts list after a failed load would otherwise offer a fresh Closing Wizard
  // over a close that already exists, skipping the Redo confirm and role check.
  const [countsReadyFor, setCountsReadyFor] = useState<string | null>(null);
  const countsReady = countsReadyFor === currentDate;

  // Monthly State — initialized immediately with default summary
  const [monthlyData, setMonthlyData] = useState<CashDrawerMonthlyResponse>(() =>
    getDefaultMonthlyData(new Date().getFullYear(), new Date().getMonth() + 1)
  );
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  // Messages
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Active Counting Wizard Modal state
  const [countModal, setCountModal] = useState<{
    device_id: string;
    device_name: string;
    shift_id?: string;
    count_type: "open" | "close";
    standard_bank_cents: number;
    bank_breakdown?: Record<string, number> | null;
  } | null>(null);

  const [denoms, setDenoms] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState("");
  const [bagNumber, setBagNumber] = useState("");
  const [confirmedBag, setConfirmedBag] = useState(false);
  const [submittingCount, setSubmittingCount] = useState(false);
  const [showSafeSlipModal, setShowSafeSlipModal] = useState(false);
  // Shown after a closing count saves. The Square Paid Out step lives here, not
  // in the wizard: sending staff to the Square app with an unsaved count in
  // memory is how Library's wizard lost counts (its 8abbe5e4 critique P0).
  const [closeReceipt, setCloseReceipt] = useState<{
    device_name: string;
    deposit_cents: number;
    retained_cents: number;
    bag_number: string;
    replaced_earlier_close: boolean;
  } | null>(null);

  // Change Swap Modal state (Morning refresh after breaking bills)
  const [changeSwapModal, setChangeSwapModal] = useState<{
    bank: CashDrawerBank;
    count: CashDrawerCountRow;
    assessment: ChangeSwapAssessment;
  } | null>(null);
  const [swapDenoms, setSwapDenoms] = useState<Record<string, number>>({});
  const [swapNotes, setSwapNotes] = useState("");
  const [submittingSwap, setSubmittingSwap] = useState(false);

  const handleOpenChangeSwapModal = (
    bank: CashDrawerBank,
    count: CashDrawerCountRow,
    assessment: ChangeSwapAssessment
  ) => {
    setChangeSwapModal({ bank, count, assessment });
    const initial = { ...(count.retained_breakdown || count.denomination_breakdown || {}) };
    setSwapDenoms(initial);
    setSwapNotes(
      assessment.largeBills.length > 0
        ? `Exchanged ${assessment.largeBills.map((b) => `${b.count}x ${b.label}`).join(" & ")} at safe change fund for smaller bills.`
        : "Refreshed small change float from safe."
    );
  };

  const swapCountedTotalCents = useMemo(() => denomTotalCents(swapDenoms), [swapDenoms]);

  const handleSubmitChangeSwap = async () => {
    if (!changeSwapModal) return;
    // A swap is recorded as the day's opening count, and the server files it
    // under ITS current business day. Same rollover guard as handleSubmitCount.
    const serverDay = cashBusinessDate();
    if (serverDay !== currentDate) {
      alert(
        `Not submitted. A change swap is saved as the opening count for the current business day (${serverDay}), not ${currentDate}. Go to today and record the swap there.`
      );
      return;
    }
    setSubmittingSwap(true);
    try {
      await submitCashDrawerCount({
        action: "count",
        device_id: changeSwapModal.bank.device_id,
        device_name: changeSwapModal.bank.device_name,
        count_type: "open",
        total_cents: swapCountedTotalCents,
        denomination_breakdown: swapDenoms,
        notes: `[Change Swap by ${currentUser?.display_label || currentUser?.first_name || "Manager"}] ${swapNotes.trim()}`,
      });

      setChangeSwapModal(null);
      showToast(`✓ Change swap recorded for ${changeSwapModal.bank.device_name}. Float is refreshed.`);
      await loadDaily();
    } catch (err: any) {
      alert("Failed to record change swap: " + (err?.message || "Error"));
    } finally {
      setSubmittingSwap(false);
    }
  };

  const showToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  // After a person verifies a bag or records a bank run, bring the top of the
  // page (and its confirmation banner) back into view. Not after auto-verify.
  const pageTopRef = useRef<HTMLDivElement | null>(null);
  const scrollToTop = () => pageTopRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  // Bumped after a bank run so the Monthly tab's history reloads.
  const [runsVersion, setRunsVersion] = useState(0);

  const loadDaily = async () => {
    setDailyLoading(true);
    setErrorMsg(null);
    setCountsReadyFor((prev) => (prev === currentDate ? prev : null));
    try {
      const [dRes, pbRes] = await Promise.allSettled([
        getCashDrawerDaily(currentDate),
        getPendingBankDeposit(),
      ]);
      let hadFailure = false;
      if (dRes.status === "fulfilled" && dRes.value && typeof dRes.value === "object") {
        setDailyData({
          summary: dRes.value.summary || getDefaultDailyData(currentDate).summary,
          shifts: Array.isArray(dRes.value.shifts) ? dRes.value.shifts : [],
          banks: (Array.isArray(dRes.value.banks) && dRes.value.banks.length > 0
            ? dRes.value.banks
            : getDefaultDailyData(currentDate).banks
          )
            .map((b) => ({
              ...b,
              device_name: getCleanDeviceName(b.device_name),
            }))
            .sort((a, b) => registerRank(a.device_name) - registerRank(b.device_name)),
          counts: Array.isArray(dRes.value.counts) ? dRes.value.counts : [],
          events: Array.isArray(dRes.value.events) ? dRes.value.events : [],
        });
        setCountsReadyFor(currentDate);
      } else {
        hadFailure = true;
        setCountsReadyFor(null);
        setDailyData(getDefaultDailyData(currentDate));
      }
      // Managers and owners only: anyone else gets { ok:false } and simply sees no
      // bank-run panel, so this failure is not a ledger outage.
      setPendingBank(pbRes.status === "fulfilled" ? pbRes.value : null);
      if (hadFailure) {
        setErrorMsg("⚠️ Live Ledger Sync Failed: Server unreachable. Displaying local estimates — cash reconciliation is restricted until connection is restored.");
      }
    } catch (err: any) {
      console.warn("Failed to load daily cash data:", err);
      setCountsReadyFor(null);
      setDailyData(getDefaultDailyData(currentDate));
      setErrorMsg("⚠️ Network Outage: Unable to load cash drawer data from server.");
    } finally {
      setDailyLoading(false);
    }
  };

  const loadMonthly = async () => {
    setMonthlyLoading(true);
    setErrorMsg(null);
    // The bank-run panel also lives on the Monthly tab. Managers/owners only;
    // anyone else gets { ok:false } and no panel, not an outage.
    getPendingBankDeposit()
      .then(setPendingBank)
      .catch(() => setPendingBank(null));
    try {
      const res = await getCashDrawerMonthly(monthYear.year, monthYear.month);
      if (res && typeof res === "object") {
        setMonthlyData({
          year: res.year || monthYear.year,
          month: res.month || monthYear.month,
          summary: res.summary || getDefaultMonthlyData(monthYear.year, monthYear.month).summary,
          days: Array.isArray(res.days) ? res.days : [],
        });
      } else {
        setMonthlyData(getDefaultMonthlyData(monthYear.year, monthYear.month));
        setErrorMsg("⚠️ Monthly Ledger Sync Failed: Server unreachable.");
      }
    } catch (err: any) {
      console.warn("Failed to load monthly cash data:", err);
      setMonthlyData(getDefaultMonthlyData(monthYear.year, monthYear.month));
      setErrorMsg("⚠️ Network Outage: Unable to load monthly cash ledger.");
    } finally {
      setMonthlyLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "daily") loadDaily();
    else loadMonthly();
  }, [tab, currentDate, monthYear]);

  // Date Navigation
  const shiftDay = (delta: number) => {
    const d = new Date(currentDate + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setCurrentDate(d.toISOString().split("T")[0]);
  };

  const shiftMonth = (delta: number) => {
    setMonthYear((prev) => {
      let m = prev.month + delta;
      let y = prev.year;
      if (m > 12) {
        m = 1;
        y += 1;
      } else if (m < 1) {
        m = 12;
        y -= 1;
      }
      return { year: y, month: m };
    });
  };

  // Denominations Total Calculation
  const countedTotalCents = useMemo(() => denomTotalCents(denoms), [denoms]);

  // The day's drawers with any drawer left open for days cut down to the cash
  // taken on THIS business day (splitHeldOverCash). Used for what each bag should
  // hold; the Square close check keeps Square's real drawer totals.
  const bagDay = useMemo(() => {
    const events = dailyData.events || [];
    const heldOver: Record<string, HeldOverDay[]> = {};
    const shifts = (dailyData.shifts || []).map((s) => {
      const r = splitHeldOverCash(s, events, (iso) => cashBusinessDate(new Date(iso)));
      if (r.byDay.length) heldOver[s.id] = r.byDay;
      return r.shift;
    });
    return { shifts, heldOver };
  }, [dailyData]);

  // Dynamic Monthly Totals Calculation
  const monthlyTotals = useMemo(() => {
    const days = monthlyData?.days || [];
    let sales = 0;
    let paidIn = 0;
    let paidOut = 0;
    let deposits = 0;
    let variance = 0;
    let verifiedCount = 0;

    for (const d of days) {
      sales += d.cash_sales_cents || 0;
      paidIn += d.paid_in_cents || 0;
      paidOut += d.paid_out_cents || 0;
      deposits += d.deposit_cents || 0;
      variance += d.variance_cents || 0;
      if (!d.has_unverified_bags) verifiedCount++;
    }

    return {
      sales,
      paidIn,
      paidOut,
      deposits,
      variance,
      verifiedCount,
      totalDays: days.length,
    };
  }, [monthlyData]);

  // Morning Change Swap Alerts (Flagged registers with large bills or depleted change)
  const morningSwapAlerts = useMemo(() => {
    if (!dailyData || !dailyData.banks) return [];
    const alerts: Array<{
      bank: CashDrawerBank;
      count: CashDrawerCountRow;
      assessment: ChangeSwapAssessment;
    }> = [];

    for (const bank of dailyData.banks) {
      const openCount = (dailyData.counts || []).find(
        (c) => c.device_id === bank.device_id && (c.count_type === "open" || c.count_type === "opening")
      );
      // Swaps are recorded as the day's opening, so only an opening count can drive one.
      const targetCount = openCount;
      if (targetCount) {
        const assessment = detectDrawerChangeSwapNeeded(bank, targetCount);
        if (assessment.needsSwap) {
          alerts.push({ bank, count: targetCount, assessment });
        }
      }
    }

    return alerts;
  }, [dailyData]);

  // Retained & Deposit split calculation for Closing.
  // Everything above the standard bank goes to the deposit bag; the drawer keeps
  // the bank EXACTLY (smallest change preferred) or, when the counted mix cannot
  // make it, as much as it can with the rest declared as a shortfall. The edge
  // function rejects any split where retained + shortfall !== standard bank, so
  // the math lives in src/lib/cashSplit.ts where it is unit-tested.
  const closingSplit = useMemo(() => {
    if (!countModal || countModal.count_type !== "close") return null;
    const stdBank = countModal.standard_bank_cents || 30000;
    return computeClosingSplit(denoms, stdBank);
  }, [countModal, denoms]);

  const hasOpeningToday = (deviceId: string) =>
    (dailyData.counts || []).some(
      (c) => c.device_id === deviceId && (c.count_type === "open" || c.count_type === "opening")
    );

  const handleOpenCountWizard = (
    bank: CashDrawerBank,
    shift: CashDrawerShift | undefined,
    countType: "open" | "close"
  ) => {
    setCountModal({
      device_id: bank.device_id,
      device_name: bank.device_name,
      shift_id: shift?.id,
      count_type: countType,
      standard_bank_cents: bank.standard_bank_cents,
      bank_breakdown: bank.bank_breakdown,
    });
    setDenoms({});
    setNotes("");
    setBagNumber("");
    setConfirmedBag(false);
  };

  // Re-run an opening or closing count over one already recorded today. The
  // server replaces the existing (business day, register, count type) row; a
  // unique index on those three columns guarantees there is only one. A $0
  // count records nothing, so anyone may replace it; replacing a real count
  // is a lead/manager/owner call. There is no way to delete a count.
  const handleRedoCount = (
    bank: CashDrawerBank,
    shift: CashDrawerShift | undefined,
    existing: CashDrawerCountRow,
    countType: "open" | "close"
  ) => {
    const label = countType === "open" ? "opening" : "closing";
    const who = existing.counted_by_name ? ` by ${existing.counted_by_name}` : "";
    const when = existing.created_at
      ? ` at ${new Date(existing.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
      : "";
    const bag = existing.deposit_cents
      ? `

It recorded a ${fmtCents(existing.deposit_cents)} deposit in bag #${existing.deposit_bag_number || "?"}. Make sure the bag matches the new count.`
      : "";
    const ok = window.confirm(
      `A${countType === "open" ? "n" : ""} ${label} count of ${fmtCents(existing.total_cents || 0)} is already recorded for ${getCleanDeviceName(bank.device_name)} today${who}${when}. A new count replaces it.${bag}

Redo the ${label} count?`
    );
    if (ok) handleOpenCountWizard(bank, shift, countType);
  };

  const handleSubmitCount = async () => {
    if (!countModal) return;
    // The server files every count under ITS current business day and ignores
    // the date on screen. If the day rolled over at 3:00 AM while this wizard
    // was open, submitting would land the count on the wrong day.
    const serverDay = cashBusinessDate();
    if (serverDay !== currentDate) {
      alert(
        `Not submitted. The cash business day rolled over at 3:00 AM, so this count would be saved under ${serverDay} instead of ${currentDate}.

Write the count down and tell a manager so the ${currentDate} count can be recorded by hand.`
      );
      return;
    }
    setSubmittingCount(true);
    try {
      let payload: any = {
        action: "count",
        business_date: currentDate,
        device_id: countModal.device_id,
        shift_id: countModal.shift_id,
        count_type: countModal.count_type,
        total_cents: countedTotalCents,
        denomination_breakdown: denoms,
        notes: notes.trim() || undefined,
      };
      // A close with no opening count today: say so on the record.
      if (countModal.count_type === "close" && !hasOpeningToday(countModal.device_id)) {
        payload.notes = ["[No opening count today]", notes.trim()].filter(Boolean).join(" ");
      }

      if (countModal.count_type === "close" && closingSplit) {
        if (closingSplit.depositTotal > 0 && !bagNumber.trim()) {
          alert("Deposit bag number is required.");
          setSubmittingCount(false);
          return;
        }
        if (closingSplit.depositTotal > 0 && !confirmedBag) {
          alert("Please confirm that the deposit bag has been sealed.");
          setSubmittingCount(false);
          return;
        }
        payload = {
          ...payload,
          retained_breakdown: closingSplit.retained,
          retained_cents: closingSplit.retainedTotal,
          deposit_breakdown: closingSplit.deposit,
          deposit_cents: closingSplit.depositTotal,
          bank_shortfall_cents: closingSplit.shortfall,
          deposit_bag_number: bagNumber.trim() || undefined,
          deposit_confirmed_at: confirmedBag ? new Date().toISOString() : undefined,
        };
      }

      const replacedEarlierClose = (dailyData.counts || []).some(
        (c) => c.device_id === countModal.device_id && (c.count_type === "close" || c.count_type === "closing")
      );
      await submitCashDrawerCount(payload);
      setCountModal(null);
      if (countModal.count_type === "close") {
        setCloseReceipt({
          device_name: getCleanDeviceName(countModal.device_name),
          deposit_cents: closingSplit?.depositTotal || 0,
          retained_cents: closingSplit?.retainedTotal || 0,
          bag_number: bagNumber.trim(),
          replaced_earlier_close: replacedEarlierClose,
        });
      } else {
        showToast(`Opening count recorded for ${countModal.device_name}.`);
      }
      await loadDaily();
    } catch (err: any) {
      alert("Submission error: " + (err?.message || "Failed"));
    } finally {
      setSubmittingCount(false);
    }
  };

  const fmtCents = (cents: number | null | undefined) =>
    "$" + ((cents || 0) / 100).toFixed(2);
  const fmtSigned = (cents: number | null | undefined) => {
    const c = cents || 0;
    return (c < 0 ? "-" : c > 0 ? "+" : "") + fmtCents(Math.abs(c));
  };
  const fmtDateLabel = (dStr: string) => {
    try {
      return new Date(dStr + "T12:00:00").toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dStr;
    }
  };

  if (currentUser && (currentUser.is_tablet || currentUser.email?.startsWith("station-tablet-"))) {
    return (
      <div style={styles.tabletGuardContainer}>
        <div style={styles.tabletGuardCard}>
          <div style={styles.tabletGuardIcon}>🔒</div>
          <h2 style={styles.tabletGuardTitle}>Individual Staff Sign-In Required</h2>
          <p style={styles.tabletGuardText}>
            Cash drawer counts, drops, and reconciliations require individual staff attribution to record who processed each transaction.
          </p>
          <p style={styles.tabletGuardSubText}>
            Please sign in to Station on your personal mobile phone (via SMS/Email OTP) to manage the cash drawer.
          </p>
          <button
            type="button"
            onClick={() => navigate("/station")}
            style={styles.tabletGuardBtn}
          >
            ← Return to Station
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} ref={pageTopRef}>
      {/* Header */}
      <div style={isMobile ? styles.headerMobile : styles.header}>
        <div>
          <h1 style={styles.title}>Cash Management</h1>
          <p style={styles.subtitle}>
            Daily drawer counts, denomination calculator, and safe deposit ledger
          </p>
        </div>
        <div style={isMobile ? styles.tabToggleMobile : styles.tabToggle}>
          <button
            style={{
              ...styles.tabBtn,
              ...(tab === "daily" ? styles.tabBtnActive : {}),
            }}
            onClick={() => setTab("daily")}
          >
            Daily
          </button>
          <button
            style={{
              ...styles.tabBtn,
              ...(tab === "monthly" ? styles.tabBtnActive : {}),
            }}
            onClick={() => setTab("monthly")}
          >
            Monthly
          </button>
        </div>
      </div>

      {/* Messages */}
      {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}
      {successMsg && <div style={styles.successBanner}>{successMsg}</div>}

      {/* ═══ DAILY VIEW ═══ */}
      {tab === "daily" && (
        <div>
          {/* Date Navigator */}
          <div style={isMobile ? styles.dateNavMobile : styles.dateNav}>
            <button style={styles.navBtn} onClick={() => shiftDay(-1)}>
              ← Prev Day
            </button>
            <div style={styles.dateDisplay}>
              <span style={styles.dateText}>{fmtDateLabel(currentDate)}</span>
              <label style={styles.calendarPickerBtn} title="Select date from calendar">
                <span style={{ fontSize: 15, cursor: "pointer" }}>📅</span>
                <input
                  type="date"
                  style={styles.hiddenDateInput}
                  value={currentDate}
                  onChange={(e) => setCurrentDate(e.target.value)}
                />
              </label>
            </div>
            <button style={styles.navBtn} onClick={() => shiftDay(1)}>
              Next Day →
            </button>
            {dailyLoading && (
              <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: 8 }}>
                🔄 Syncing live registers...
              </span>
            )}
          </div>

          {!isBizToday && (
            <div style={styles.pastDayNotice}>
              <span>
                Viewing {fmtDateLabel(currentDate)}. Opening and closing counts can only be recorded for the current business day ({fmtDateLabel(bizToday)}), because the server saves every count under today's date.
              </span>
              <button type="button" style={styles.actionBtnSmall} onClick={() => setCurrentDate(bizToday)}>
                Go to today
              </button>
            </div>
          )}

          {dailyData && (
            <>
              {/* Daily KPIs */}
              <div style={isMobile ? styles.kpiGridMobile : styles.kpiGrid}>
                <div style={styles.kpiCard}>
                  <div style={styles.kpiVal}>
                    {fmtCents(dailyData.summary?.total_cash_sales_cents)}
                  </div>
                  <div style={styles.kpiLabel}>Cash Sales</div>
                </div>
                <div style={styles.kpiCard}>
                  <div style={styles.kpiVal}>
                    {fmtCents(dailyData.summary?.total_tips_cents)}
                  </div>
                  <div style={styles.kpiLabel}>Tips Collected</div>
                </div>
                <div style={styles.kpiCard}>
                  <div style={styles.kpiVal}>
                    {fmtCents(dailyData.summary?.total_deposit_cents)}
                  </div>
                  <div style={styles.kpiLabel}>Nightly Deposit</div>
                </div>
                <div style={styles.kpiCard}>
                  <div
                    style={{
                      ...styles.kpiVal,
                      color:
                        (dailyData.summary?.total_variance_cents || 0) === 0
                          ? "var(--green, #27ae60)"
                          : "var(--red, #E05040)",
                    }}
                  >
                    {fmtSigned(dailyData.summary?.total_variance_cents)}
                  </div>
                  <div style={styles.kpiLabel}>Cash Variance</div>
                </div>
              </div>

              {/* Morning Change Swap Alert Banner (Skewed Float from Breaking Bills) */}
              {morningSwapAlerts.length > 0 && (
                <div style={styles.morningSwapAlertCard}>
                  <div style={styles.morningSwapAlertHeader}>
                    <div style={styles.morningSwapIconBadge}>🔄</div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.morningSwapTitle}>
                        Morning Bank Float Swap Required ({morningSwapAlerts.length} register{morningSwapAlerts.length > 1 ? "s" : ""})
                      </div>
                      <div style={styles.morningSwapDesc}>
                        Staff broke bills or depleted change during previous service. Designated opening staff should exchange large bills for small change at the safe before opening.
                      </div>
                    </div>
                  </div>

                  <div style={styles.morningSwapRegistersList}>
                    {morningSwapAlerts.map(({ bank, count, assessment }) => (
                      <div key={bank.device_id} style={styles.morningSwapItem}>
                        <div style={{ flex: 1 }}>
                          <div style={styles.morningSwapItemName}>
                            <strong>{getCleanDeviceName(bank.device_name)}:</strong> <span style={{ color: "var(--gold)" }}>{assessment.summary}</span>
                          </div>
                          <div style={styles.morningSwapItemRec}>
                            {assessment.recommendation}
                          </div>
                        </div>
                        {isBizToday && countsReady && (
                          <button
                            type="button"
                            style={styles.completeSwapBtn}
                            onClick={() => handleOpenChangeSwapModal(bank, count, assessment)}
                          >
                            🔄 Record Swap
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Till Drawers Grid */}
              <div style={styles.sectionHeading}>Registers & Drawer Tills</div>
              <div style={isMobile ? styles.drawersGridMobile : styles.drawersGrid}>
                {(dailyData.banks || []).map((bank) => {
                  const shift = (dailyData.shifts || []).find(
                    (s) => s.device_id === bank.device_id
                  );
                  const openCount = (dailyData.counts || []).find(
                    (c) => c.device_id === bank.device_id && (c.count_type === "open" || c.count_type === "opening")
                  );
                  const closeCount = (dailyData.counts || []).find(
                    (c) => c.device_id === bank.device_id && (c.count_type === "close" || c.count_type === "closing")
                  );

                  const sales = registerCashSales(bagDay.shifts, bank.device_id);
                  const heldOverCents = bagDay.shifts
                    .filter((s) => s.device_id === bank.device_id)
                    .reduce((sum, s) => sum + (s.held_over_cents || 0), 0);
                  // Swaps are recorded as the day's opening, so only an opening count can drive one.
                  const targetCount = openCount;
                  const swapAssessment = targetCount ? detectDrawerChangeSwapNeeded(bank, targetCount) : null;

                  const isOpen = shift?.state === "OPEN";
                  const isClosed = shift?.state === "CLOSED";

                  return (
                    <div key={bank.device_id} style={styles.tillCard}>
                      <div style={styles.tillHeader}>
                        <div>
                          <div style={styles.tillName}>{getCleanDeviceName(bank.device_name)}</div>
                          <div style={styles.tillSub}>
                            Standard Bank: {fmtCents(bank.standard_bank_cents)}
                          </div>
                          {(() => {
                            // Who to call about this register's count: Square's drawer record.
                            const staff = drawerStaff(dailyData.shifts || [], bank.device_id);
                            if (!staff.openedBy && !staff.closedBy) return null;
                            return (
                              <div style={styles.tillSub}>
                                Square: opened by {staff.openedBy || "—"}
                                {staff.closedBy ? ` · closed by ${staff.closedBy}` : ""}
                              </div>
                            );
                          })()}
                        </div>
                        <span
                          style={{
                            ...styles.statusBadge,
                            ...(isOpen
                              ? styles.badgeOpen
                              : isClosed
                              ? styles.badgeClosed
                              : styles.badgeUnopened),
                          }}
                        >
                          {isOpen ? "Open" : isClosed ? "Closed" : "Unopened"}
                        </span>
                      </div>

                      {/* Counts History */}
                      <div style={styles.tillBody}>
                        {/* What the deposit bag should hold: the register's Square cash sales
                            (the back office's "Deposit bag → safe"; same rule as bag verification). */}
                        {(sales.closedShifts > 0 || sales.openCents != null || closeCount) && (
                          <div style={styles.depositPanel}>
                            <div style={styles.depositPanelLabel}>Deposit bag should hold</div>
                            <div style={styles.depositPanelAmt}>
                              {sales.closedShifts > 0 ? fmtCents(sales.closedCents) : "—"}
                            </div>
                            <div style={styles.depositPanelSub}>
                              {sales.closedShifts > 1
                                ? `Square cash sales across ${sales.closedShifts} drawer shifts (one bag each)`
                                : sales.closedShifts === 1
                                ? "Square cash sales for this register"
                                : sales.openCents == null
                                ? "Set once the drawer closes in Square"
                                : ""}
                              {sales.openCents != null &&
                                `${sales.closedShifts > 0 ? " · " : ""}Drawer still open in Square, cash sales so far ${fmtCents(sales.openCents)}`}
                            </div>
                            {heldOverCents > 0 && (
                              <div style={styles.depositPanelWarn}>
                                ⚠ Drawer left open in Square: {fmtCents(heldOverCents)} taken on later days is not
                                counted here (see its bag below).
                              </div>
                            )}
                            {closeCount && closeCount.deposit_cents != null && sales.closedShifts === 1 &&
                              closeCount.deposit_cents !== sales.closedCents && (
                                <div style={styles.depositPanelWarn}>
                                  ⚠ Closer bagged {fmtCents(closeCount.deposit_cents)}:{" "}
                                  {fmtSigned(closeCount.deposit_cents - sales.closedCents)} vs cash sales
                                </div>
                              )}
                          </div>
                        )}
                        <div style={styles.countRow}>
                          <span>Opening Count:</span>
                          <strong>
                            {openCount ? fmtCents(openCount.total_cents) : "Not Counted"}
                          </strong>
                        </div>
                        {openCount && (
                          <div style={{ fontSize: 10, color: "var(--muted, #8A7A66)", textAlign: "right", marginTop: -4 }}>
                            {openCount.counted_by_name ? `By ${openCount.counted_by_name} · ` : ""}
                            {openCount.created_at ? new Date(openCount.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                          </div>
                        )}
                        <div style={styles.countRow}>
                          <span>Closing Count:</span>
                          <strong>
                            {closeCount ? fmtCents(closeCount.total_cents) : "Not Counted"}
                          </strong>
                        </div>
                        {closeCount && (
                          <div style={{ fontSize: 10, color: "var(--muted, #8A7A66)", textAlign: "right", marginTop: -4 }}>
                            {closeCount.counted_by_name ? `By ${closeCount.counted_by_name} · ` : ""}
                            {closeCount.created_at ? new Date(closeCount.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                          </div>
                        )}
                        {closeCount && closeCount.deposit_cents !== undefined && (
                          <div style={styles.countRow}>
                            <span>Deposit Bag:</span>
                            <span style={{ color: "var(--gold, #F9AF36)", fontWeight: 700 }}>
                              {fmtCents(closeCount.deposit_cents)} (Bag #{closeCount.deposit_bag_number || "—"})
                            </span>
                          </div>
                        )}
                        {closeCount && (
                          <div style={styles.squareCheck} aria-label="Square close check">
                            {squareCloseCheck(squareCloseInput(closeCount, bank.device_id, dailyData.shifts || [])).map((line, i) => (
                              <div key={i} style={{ ...styles.squareCheckLine, color: CHECK_COLOR[line.tone] }}>
                                <span aria-hidden="true" style={styles.squareCheckIcon}>{CHECK_ICON[line.tone]}</span>
                                <span>{line.text}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Change Swap Warning if large bills / skewed float detected */}
                        {swapAssessment?.needsSwap && (
                          <div style={styles.tillSwapBanner}>
                            <div style={styles.tillSwapHeader}>
                              <span>🔄 <strong>Change Swap Needed:</strong> {swapAssessment.summary}</span>
                            </div>
                            <div style={styles.tillSwapRec}>
                              {swapAssessment.recommendation}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Till Action Buttons */}
                      <div style={styles.tillActions}>
                        {isBizToday && countsReady && swapAssessment?.needsSwap && targetCount && (
                          <button
                            type="button"
                            style={styles.changeSwapBtnSmall}
                            onClick={() => handleOpenChangeSwapModal(bank, targetCount, swapAssessment)}
                            title="Swap large bills at safe for change"
                          >
                            🔄 Swap Float
                          </button>
                        )}
                        {isBizToday && countsReady && !openCount && !closeCount && (
                          <button
                            style={styles.goldBtnSmall}
                            onClick={() => handleOpenCountWizard(bank, shift, "open")}
                          >
                            📝 Opening Count
                          </button>
                        )}
                        {/* The closer can close even when nobody counted the opening;
                            the server never required one. */}
                        {isBizToday && countsReady && !closeCount && (
                          <button
                            // Opening first: while the opening is uncounted the close is the
                            // secondary (outlined) action; once it is counted, the close leads.
                            style={openCount ? styles.goldBtnSmall : styles.goldOutlineBtnSmall}
                            onClick={() => handleOpenCountWizard(bank, shift, "close")}
                          >
                            🔒 Closing Wizard
                          </button>
                        )}
                        {isBizToday && countsReady && closeCount && (isOwnerOrManager || !(closeCount.total_cents > 0)) && (
                          <button
                            type="button"
                            style={closeCount.total_cents > 0 ? styles.actionBtnSmall : styles.goldBtnSmall}
                            title="Count the drawer again and replace today's closing count"
                            onClick={() => handleRedoCount(bank, shift, closeCount, "close")}
                          >
                            🔁 Redo Closing Count
                          </button>
                        )}
                        {isBizToday && countsReady && openCount && (isOwnerOrManager || !(openCount.total_cents > 0)) && (
                          <button
                            type="button"
                            style={openCount.total_cents > 0 ? styles.actionBtnSmall : styles.goldBtnSmall}
                            title="Count the drawer again and replace today's opening count"
                            onClick={() => handleRedoCount(bank, shift, openCount, "open")}
                          >
                            🔁 Redo Opening Count
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Who to call about this day's counts: Square clock-ins (managers/owners). */}
              {canVerifyBags && <OnShiftPanel businessDate={currentDate} />}

              <DepositBagsSection
                shifts={orderByRegister(bagDay.shifts, (dailyData.banks || []).map((b) => b.device_id))}
                heldOver={bagDay.heldOver}
                counts={dailyData.counts || []}
                canVerify={canVerifyBags}
                canReopen={canReopenBags}
                deviceLabel={getCleanDeviceName}
                onChanged={loadDaily}
                afterUserAction={scrollToTop}
                notify={showToast}
              />

              {/* Bank run last on the day page: bags are verified first, then taken to the bank. */}
              {canVerifyBags && pendingBank && (
                <div style={{ marginTop: 24 }}>
                  <BankRunPanel
                    pending={pendingBank}
                    onRecorded={async () => {
                      setRunsVersion((v) => v + 1);
                      await loadDaily();
                      scrollToTop();
                    }}
                    notify={showToast}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══ MONTHLY VIEW ═══ */}
      {tab === "monthly" && (
        <div>
          {/* Month Navigator */}
          <div style={isMobile ? styles.dateNavMobile : styles.dateNav}>
            <button style={styles.navBtn} onClick={() => shiftMonth(-1)}>
              ← Prev Month
            </button>
            <div style={styles.dateText}>
              {new Date(monthYear.year, monthYear.month - 1, 1).toLocaleDateString(
                "en-US",
                { month: "long", year: "numeric" }
              )}
            </div>
            <button style={styles.navBtn} onClick={() => shiftMonth(1)}>
              Next Month →
            </button>
            {monthlyLoading && (
              <span style={{ fontSize: 11, color: "var(--muted)", marginLeft: 8 }}>
                🔄 Syncing monthly ledger...
              </span>
            )}
          </div>

          {monthlyData && (
            <>
              {canVerifyBags && pendingBank && (
                <BankRunPanel
                  pending={pendingBank}
                  onRecorded={async () => {
                    setRunsVersion((v) => v + 1);
                    await loadMonthly();
                    scrollToTop();
                  }}
                  notify={showToast}
                />
              )}

              {/* Monthly KPIs */}
              <div style={isMobile ? styles.kpiGridMobile : styles.kpiGrid}>
                <div style={styles.kpiCard}>
                  <div style={styles.kpiVal}>
                    {fmtCents(monthlyTotals.sales || monthlyData.summary?.total_sales_cents)}
                  </div>
                  <div style={styles.kpiLabel}>Total Cash Sales</div>
                </div>
                <div style={styles.kpiCard}>
                  <div style={styles.kpiVal}>
                    {fmtCents(monthlyTotals.deposits || monthlyData.summary?.total_deposits_cents)}
                  </div>
                  <div style={styles.kpiLabel}>Total Deposits</div>
                </div>
                <div style={styles.kpiCard}>
                  <div
                    style={{
                      ...styles.kpiVal,
                      color:
                        (monthlyTotals.variance || monthlyData.summary?.total_variance_cents || 0) === 0
                          ? "var(--green, #27ae60)"
                          : "var(--red, #E05040)",
                    }}
                  >
                    {fmtSigned(monthlyTotals.variance || monthlyData.summary?.total_variance_cents)}
                  </div>
                  <div style={styles.kpiLabel}>Net Variance</div>
                </div>
                <div style={styles.kpiCard}>
                  <div style={{ ...styles.kpiVal, color: "var(--gold)" }}>
                    {monthlyTotals.verifiedCount} / {monthlyTotals.totalDays}
                  </div>
                  <div style={styles.kpiLabel}>Verified Days</div>
                </div>
              </div>

              {/* Monthly Table */}
              <div style={styles.sectionHeading}>Daily Breakdown</div>
              <div style={styles.tableCard}>
                <div style={styles.tableResponsiveWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Cash Sales</th>
                        <th style={styles.th}>Paid In / Out</th>
                        <th style={styles.th}>Deposit</th>
                        <th style={styles.th}>Variance</th>
                        <th style={styles.th}>Status</th>
                        <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(monthlyData.days || []).map((day) => (
                        <tr key={day.business_date} style={styles.tr}>
                          <td style={{ ...styles.td, fontWeight: 700 }}>
                            {fmtDateLabel(day.business_date)}
                          </td>
                          <td style={styles.td}>{fmtCents(day.cash_sales_cents)}</td>
                          <td style={styles.td}>
                            +{fmtCents(day.paid_in_cents)} / -{fmtCents(day.paid_out_cents)}
                          </td>
                          <td style={{ ...styles.td, color: "var(--gold)" }}>
                            {fmtCents(day.deposit_cents)}
                          </td>
                          <td
                            style={{
                              ...styles.td,
                              color:
                                (day.variance_cents || 0) === 0
                                  ? "var(--green, #27ae60)"
                                  : "var(--red, #E05040)",
                            }}
                          >
                            {fmtSigned(day.variance_cents)}
                          </td>
                          <td style={styles.td}>
                            {day.banked && day.bank_run_status === "stale" ? (
                              <span style={{ ...styles.badgeWarn, color: "var(--red, #E05040)" }}>Bank run needs re-verification</span>
                            ) : day.banked ? (
                              <span style={styles.badgeOk}>Banked</span>
                            ) : !day.has_unverified_bags ? (
                              <span style={styles.badgeOk}>Verified</span>
                            ) : day.drawers_closed === 0 ? (
                              <span style={{ ...styles.badgeWarn, color: "var(--muted, #8A7A66)" }}>No drawers closed</span>
                            ) : (
                              <span style={styles.badgeWarn}>Needs bag verification</span>
                            )}
                          </td>
                          <td style={{ ...styles.td, textAlign: "right" }}>
                            <button
                              type="button"
                              style={styles.actionBtnSmall}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCurrentDate(day.business_date);
                                setTab("daily");
                              }}
                            >
                              {canVerifyBags && day.has_unverified_bags && day.drawers_closed > 0 ? "Verify bags →" : "Inspect Day →"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {monthlyTotals.totalDays > 0 && (
                      <tfoot>
                        <tr style={{ ...styles.tr, background: "rgba(249, 175, 54, 0.08)", borderTop: "2px solid var(--border)" }}>
                          <td style={{ ...styles.td, fontWeight: 800, color: "var(--gold)" }}>
                            Monthly Totals ({monthlyTotals.totalDays}d)
                          </td>
                          <td style={{ ...styles.td, fontWeight: 800, color: "var(--gold)" }}>
                            {fmtCents(monthlyTotals.sales)}
                          </td>
                          <td style={{ ...styles.td, fontWeight: 700 }}>
                            +{fmtCents(monthlyTotals.paidIn)} / -{fmtCents(monthlyTotals.paidOut)}
                          </td>
                          <td style={{ ...styles.td, fontWeight: 800, color: "var(--gold)" }}>
                            {fmtCents(monthlyTotals.deposits)}
                          </td>
                          <td
                            style={{
                              ...styles.td,
                              fontWeight: 800,
                              color: monthlyTotals.variance === 0 ? "var(--green, #27ae60)" : "var(--red, #E05040)",
                            }}
                          >
                            {fmtSigned(monthlyTotals.variance)}
                          </td>
                          <td style={{ ...styles.td, fontWeight: 700 }}>
                            {monthlyTotals.verifiedCount} / {monthlyTotals.totalDays} Verified
                          </td>
                          <td style={styles.td}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              <BankRunHistory refreshKey={`${monthYear.year}-${monthYear.month}-${runsVersion}`} />
            </>
          )}
        </div>
      )}

      {/* ═══ COUNTING WIZARD MODAL ═══ */}
      {countModal && (
        <div className="mobile-sheet-backdrop" style={isMobile ? styles.modalOverlayMobile : styles.modalOverlay} onClick={() => setCountModal(null)}>
          <div className="mobile-sheet-content" style={isMobile ? styles.modalBodyMobile : styles.modalBody} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>
                  {countModal.count_type === "open" ? "Opening Count" : "Closing Wizard"} —{" "}
                  {countModal.device_name}
                </h2>
                <p style={styles.modalMeta}>
                  Count physical bills and coins before making deposits
                </p>
              </div>
              <button style={styles.closeBtn} onClick={() => setCountModal(null)}>
                ✕
              </button>
            </div>

            {countModal.count_type === "close" && !hasOpeningToday(countModal.device_id) && (
              <div style={styles.pastDayNotice}>
                <span>
                  No opening count was recorded for this register today. Count the full drawer as usual; the
                  overnight check against last night&apos;s bank is skipped and the close is marked
                  &quot;No opening count today&quot;.
                </span>
              </div>
            )}

            {/* Step 1: Denominations Grid */}
            <div style={styles.denomSection}>
              <div style={styles.sectionHeading}>Physical Cash Count</div>

              <DenominationCounter value={denoms} onChange={setDenoms} compact={isMobile} />

              <div style={styles.countedTotalRow}>
                <span>Total Counted:</span>
                <strong style={styles.countedTotalVal}>
                  {fmtCents(countedTotalCents)}
                </strong>
              </div>
            </div>

            {/* Step 2 & 3: Closing Split & Deposit Confirmation */}
            {countModal.count_type === "close" && closingSplit && (
              <div style={styles.splitSection}>
                <div style={styles.sectionHeading}>Deposit Split Summary</div>
                <div style={styles.splitBox}>
                  <div style={styles.splitRow}>
                    <span>Leave in Drawer (Opening Bank):</span>
                    <strong>{fmtCents(closingSplit.retainedTotal)}</strong>
                  </div>
                  <div style={styles.splitRow}>
                    <span>Place in Deposit Bag:</span>
                    <strong style={{ color: "var(--gold, #F9AF36)" }}>
                      {fmtCents(closingSplit.depositTotal)}
                    </strong>
                  </div>
                  {closingSplit.shortfall > 0 && (
                    <div style={{ ...styles.splitRow, color: "var(--red, #E05040)" }}>
                      <span>Bank Shortfall:</span>
                      <strong>{fmtCents(closingSplit.shortfall)}</strong>
                    </div>
                  )}
                </div>

                {closingSplit.depositTotal === 0 && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(39, 174, 96, 0.15)", border: "1px solid rgba(39, 174, 96, 0.3)", borderRadius: 8, color: "#86efac", fontSize: 12 }}>
                    ✓ <strong>No Safe Deposit Required:</strong> Total cash ({fmtCents(countedTotalCents)}) remains in the drawer as the opening float. No deposit bag needed.
                  </div>
                )}

                {((closingSplit.retained["10000"] || 0) > 0 || (closingSplit.retained["5000"] || 0) > 0) && (
                  <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(249, 175, 54, 0.12)", border: "1px solid rgba(249, 175, 54, 0.3)", borderRadius: 8, color: "var(--gold)", fontSize: 12 }}>
                    🔄 <strong>Morning Change Swap Flagged:</strong> Retaining {closingSplit.retained["10000"] ? `${closingSplit.retained["10000"]}x $100 ` : ""}{closingSplit.retained["5000"] ? `${closingSplit.retained["5000"]}x $50 ` : ""}in drawer float. Opening lead will be notified to swap this for change at the safe tomorrow morning.
                  </div>
                )}

                {closingSplit.depositTotal > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <label style={styles.fieldLabel}>Deposit Bag Number *</label>
                      <button
                        type="button"
                        onClick={() => setShowSafeSlipModal(true)}
                        style={styles.previewSlipBtn}
                      >
                        🖨️ Safe Drop Slip
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="text"
                        style={{ ...styles.input, flex: 1, minWidth: 0 }}
                        placeholder="Scan or type the bag barcode"
                        value={bagNumber}
                        onChange={(e) => setBagNumber(e.target.value)}
                        // Handheld scanners type the code then press Enter.
                        onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                      />
                      <button
                        type="button"
                        style={{ ...styles.actionBtnSmall, flex: "0 0 auto" }}
                        onClick={() => setScanBagOpen(true)}
                        aria-label="Scan bag barcode with camera"
                      >
                        📷 Scan
                      </button>
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <label style={{ cursor: "pointer", color: "var(--text)" }}>
                        <input
                          type="checkbox"
                          checked={confirmedBag}
                          onChange={(e) => setConfirmedBag(e.target.checked)}
                        />
                        &nbsp; I have placed {fmtCents(closingSplit.depositTotal)} in the sealed deposit bag.
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div style={{ marginTop: 12 }}>
              <label style={styles.fieldLabel}>Count Notes (Optional)</label>
              <input
                type="text"
                style={styles.input}
                placeholder="Any variance notes or observations"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div style={isMobile ? styles.modalActionsMobile : styles.modalActionsDesktop}>
              <button style={styles.cancelBtn} onClick={() => setCountModal(null)}>
                Cancel
              </button>
              <button
                style={styles.saveBtn}
                disabled={submittingCount}
                onClick={handleSubmitCount}
              >
                {submittingCount ? "Submitting..." : "Submit Count"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The closer scans the sealed bag; the count saves it with their name. */}
      <BarcodeScannerModal
        isOpen={scanBagOpen}
        title="Scan Deposit Bag Barcode"
        lockOrientation={false}
        validateRetailChecksum={false}
        manualLabel="Enter bag number"
        manualPlaceholder="Bag number"
        onClose={() => setScanBagOpen(false)}
        onScan={(code) => {
          setBagNumber(code.trim());
          setScanBagOpen(false);
        }}
      />

      {/* ═══ CLOSE SAVED: NEXT STEPS (Square Paid Out) ═══ */}
      {closeReceipt && (
        <div className="mobile-sheet-backdrop" style={styles.modalOverlay} onClick={() => setCloseReceipt(null)}>
          <div className="mobile-sheet-content" style={{ ...styles.modalBody, maxWidth: 480 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="close-receipt-title">
            <div className="mobile-sheet-handle" />
            <h2 id="close-receipt-title" style={styles.modalTitle}>✓ Closing count saved</h2>
            <p style={styles.modalMeta}>{closeReceipt.device_name}</p>
            <div style={styles.splitBox}>
              {closeReceipt.deposit_cents > 0 && (
                <div style={styles.splitRow}>
                  <span>Deposit bag{closeReceipt.bag_number ? ` #${closeReceipt.bag_number}` : ""}:</span>
                  <strong style={{ color: "var(--gold, #F9AF36)" }}>{fmtCents(closeReceipt.deposit_cents)}</strong>
                </div>
              )}
              <div style={styles.splitRow}>
                <span>Stays in the drawer (tomorrow's bank):</span>
                <strong>{fmtCents(closeReceipt.retained_cents)}</strong>
              </div>
            </div>
            <ol style={styles.receiptSteps}>
              {closeReceipt.deposit_cents > 0 && <li>Put the sealed bag in the safe.</li>}
              <li>
                {closeReceipt.deposit_cents > 0 ? (
                  <>
                    In the Square register, record a <strong>Paid Out of {fmtCents(closeReceipt.deposit_cents)}</strong> (note: "Nightly deposit"), then close the drawer in Square.
                  </>
                ) : (
                  <>Close the drawer in Square.</>
                )}
              </li>
            </ol>
            {closeReceipt.replaced_earlier_close && closeReceipt.deposit_cents > 0 && (
              <p style={{ ...styles.modalMeta, color: "var(--gold, #F9AF36)" }}>
                This count replaced an earlier close. If a Paid Out for that earlier deposit is already in Square, change it to {fmtCents(closeReceipt.deposit_cents)} instead of adding a second one.
              </p>
            )}
            <p style={styles.modalMeta}>The register card shows whether Square's close matches once Square syncs.</p>
            <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
              <button type="button" style={styles.saveBtn} onClick={() => setCloseReceipt(null)} autoFocus>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PRINTABLE SAFE DROP SLIP MODAL ═══ */}
      {showSafeSlipModal && countModal && closingSplit && (
        <div className="mobile-sheet-backdrop" style={styles.modalOverlay} onClick={() => setShowSafeSlipModal(false)}>
          <div className="mobile-sheet-content" style={{ ...styles.modalBody, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>Safe Deposit Drop Slip</h2>
                <p style={styles.modalMeta}>Printable verification slip for tamper-evident deposit bag</p>
              </div>
              <button style={styles.closeBtn} onClick={() => setShowSafeSlipModal(false)}>✕</button>
            </div>

            <div className="printable-safe-slip" style={styles.safeSlipBox}>
              <div style={styles.slipHeader}>
                <div style={styles.slipLogo}>THE SENTINEL ROOM</div>
                <div style={styles.slipSub}>NIGHTLY SAFE DEPOSIT DROP SLIP</div>
              </div>

              <div style={styles.slipMetaGrid}>
                <div><strong>Date:</strong> {currentDate}</div>
                <div><strong>Time:</strong> {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                <div><strong>Register:</strong> {countModal.device_name}</div>
                <div><strong>Bag #:</strong> {bagNumber || "UNNUMBERED"}</div>
              </div>

              <div style={styles.slipTableWrap}>
                <table style={styles.slipTable}>
                  <thead>
                    <tr>
                      <th style={styles.slipTh}>Denom</th>
                      <th style={styles.slipTh}>Counted</th>
                      <th style={styles.slipTh}>Keep</th>
                      <th style={styles.slipTh}>Deposit</th>
                      <th style={styles.slipTh}>Deposit $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DENOMS.map(d => {
                      const key = String(d.cents);
                      const qty = denoms[key] || 0;
                      if (qty === 0) return null;
                      const keep = closingSplit.retained[key] || 0;
                      const bag = closingSplit.deposit[key] || 0;
                      return (
                        <tr key={d.cents} style={styles.slipTr}>
                          <td style={styles.slipTd}>{d.label}</td>
                          <td style={styles.slipTd}>{qty}</td>
                          <td style={styles.slipTd}>{keep}</td>
                          <td style={{ ...styles.slipTd, fontWeight: bag > 0 ? 700 : 400 }}>{bag}</td>
                          <td style={styles.slipTd}>{fmtCents(bag * d.cents)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={styles.slipTotals}>
                <div style={styles.slipTotalRow}>
                  <span>Total Counted:</span>
                  <strong>{fmtCents(countedTotalCents)}</strong>
                </div>
                <div style={styles.slipTotalRow}>
                  <span>Float Retained in Drawer:</span>
                  <strong>{fmtCents(closingSplit.retainedTotal)}</strong>
                </div>
                <div style={{ ...styles.slipTotalRow, borderTop: "2px solid #000", paddingTop: 6, marginTop: 6 }}>
                  <span><strong>SEALED SAFE DEPOSIT:</strong></span>
                  <strong style={{ fontSize: 16 }}>{fmtCents(closingSplit.depositTotal)}</strong>
                </div>
              </div>

              <div style={styles.slipSignatures}>
                <div style={styles.sigBlock}>
                  <div style={styles.sigLine} />
                  <span style={styles.sigLabel}>Counted By (Staff)</span>
                </div>
                <div style={styles.sigBlock}>
                  <div style={styles.sigLine} />
                  <span style={styles.sigLabel}>Witnessed By (Manager)</span>
                </div>
              </div>
            </div>

            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button style={styles.cancelBtn} onClick={() => setShowSafeSlipModal(false)}>Close</button>
              <button style={styles.saveBtn} onClick={() => window.print()}>🖨️ Print Drop Slip</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CHANGE SWAP MODAL (Morning Refresh after Breaking Bills) ═══ */}
      {changeSwapModal && (
        <div className="mobile-sheet-backdrop" style={isMobile ? styles.modalOverlayMobile : styles.modalOverlay} onClick={() => setChangeSwapModal(null)}>
          <div className="mobile-sheet-content" style={isMobile ? styles.modalBodyMobile : styles.modalBody} onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" />
            <div style={styles.modalHeader}>
              <div>
                <h2 style={styles.modalTitle}>
                  🔄 Change Swap / Float Refresh — {getCleanDeviceName(changeSwapModal.bank.device_name)}
                </h2>
                <p style={styles.modalMeta}>
                  Exchange large bills from last night's drawer with small bills & coin rolls from the safe
                </p>
              </div>
              <button style={styles.closeBtn} onClick={() => setChangeSwapModal(null)}>
                ✕
              </button>
            </div>

            {/* Assessment Recommendation Box */}
            <div style={styles.swapRecommendationCard}>
              <div style={styles.swapRecIcon}>💡</div>
              <div style={{ flex: 1 }}>
                <div style={styles.swapRecTitle}>
                  {changeSwapModal.assessment.summary || "Change Float Optimization"}
                </div>
                <div style={styles.swapRecText}>
                  {changeSwapModal.assessment.recommendation || "Exchange high denomination bills for smaller change to ensure drawer is ready for guest service."}
                </div>
              </div>
            </div>

            {/* Denomination Counter */}
            <div style={styles.denomSection}>
              <div style={styles.sectionHeading}>Updated Drawer Cash Breakdown</div>

              <DenominationCounter value={swapDenoms} onChange={setSwapDenoms} compact={isMobile} />

              <div style={styles.countedTotalRow}>
                <span>Total Float:</span>
                <strong style={{
                  ...styles.countedTotalVal,
                  color: swapCountedTotalCents === changeSwapModal.bank.standard_bank_cents ? "var(--green, #27ae60)" : "var(--gold)",
                }}>
                  {fmtCents(swapCountedTotalCents)}
                  {swapCountedTotalCents !== changeSwapModal.bank.standard_bank_cents && (
                    <span style={{ fontSize: 12, fontWeight: 400, color: "var(--muted)", marginLeft: 6 }}>
                      (Target: {fmtCents(changeSwapModal.bank.standard_bank_cents)})
                    </span>
                  )}
                </strong>
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginTop: 12 }}>
              <label style={styles.fieldLabel}>Swap Notes</label>
              <input
                type="text"
                style={styles.input}
                placeholder="e.g. Swapped 1x $50 bill from safe float for 6x $5s and 20x $1s"
                value={swapNotes}
                onChange={(e) => setSwapNotes(e.target.value)}
              />
            </div>

            <div style={isMobile ? styles.modalActionsMobile : styles.modalActionsDesktop}>
              <button style={styles.cancelBtn} onClick={() => setChangeSwapModal(null)}>
                Cancel
              </button>
              <button
                style={styles.saveBtn}
                disabled={submittingSwap}
                onClick={handleSubmitChangeSwap}
              >
                {submittingSwap ? "Saving..." : "Confirm Change Swap"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: 960,
    margin: "0 auto",
    padding: "24px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  headerMobile: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12,
    marginBottom: 16,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  tabToggle: {
    display: "flex",
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: 3,
  },
  tabToggleMobile: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: 3,
    gap: 4,
  },
  tabBtn: {
    background: "transparent",
    border: "none",
    color: "var(--muted, #8A7A66)",
    padding: "8px 14px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 40,
  },
  tabBtnActive: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "#ff8b7b",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  successBanner: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.4)",
    color: "#4ade80",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  dateNav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "10px 16px",
    marginBottom: 20,
  },
  dateNavMobile: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "8px 10px",
    marginBottom: 16,
    gap: 6,
  },
  navBtn: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 6,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    minHeight: 40,
  },
  dateDisplay: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  dateText: {
    fontFamily: "Georgia, serif",
    fontSize: 15,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  calendarPickerBtn: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 6,
    padding: "6px 10px",
    cursor: "pointer",
    lineHeight: 1,
    minHeight: 38,
  },
  hiddenDateInput: {
    position: "absolute",
    inset: 0,
    opacity: 0,
    width: "100%",
    height: "100%",
    cursor: "pointer",
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
    marginBottom: 20,
  },
  kpiGridMobile: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
    marginBottom: 16,
  },
  kpiCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "12px 8px",
    textAlign: "center",
  },
  kpiVal: {
    fontFamily: "Georgia, serif",
    fontSize: 20,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  kpiLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "var(--muted, #8A7A66)",
    marginTop: 4,
  },
  sectionHeading: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
    marginBottom: 12,
  },
  drawersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 16,
    marginBottom: 20,
  },
  drawersGridMobile: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 12,
    marginBottom: 20,
  },
  tillCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 10,
    padding: 16,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  tillHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
    borderBottom: "1px solid var(--border, #2A1F14)",
    paddingBottom: 10,
  },
  tillName: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--text, #F0E6D4)",
  },
  tillSub: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
  },
  statusBadge: {
    padding: "3px 8px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  badgeOpen: {
    background: "rgba(39, 174, 96, 0.15)",
    color: "#4ade80",
    border: "1px solid rgba(39, 174, 96, 0.4)",
  },
  badgeClosed: {
    background: "rgba(138, 122, 102, 0.15)",
    color: "var(--muted, #8A7A66)",
    border: "1px solid rgba(138, 122, 102, 0.3)",
  },
  badgeUnopened: {
    background: "rgba(249, 175, 54, 0.15)",
    color: "var(--gold, #F9AF36)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
  },
  tillBody: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginBottom: 16,
  },
  depositPanel: {
    background: "rgba(249, 175, 54, 0.08)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 8,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  depositPanelLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "var(--muted, #8A7A66)",
  },
  depositPanelAmt: { fontSize: 22, fontWeight: 800, color: "var(--gold, #F9AF36)" },
  depositPanelSub: { fontSize: 12, color: "var(--muted, #8A7A66)" },
  depositPanelWarn: { fontSize: 12, fontWeight: 700, color: "var(--red, #E05040)", marginTop: 2 },
  countRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
  },
  squareCheck: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    marginTop: 2,
    paddingTop: 6,
    borderTop: "1px dashed var(--border, #2A1F14)",
  },
  squareCheckLine: {
    display: "flex",
    gap: 6,
    fontSize: 12,
    lineHeight: 1.35,
  },
  squareCheckIcon: {
    flexShrink: 0,
    width: 14,
    textAlign: "center",
  },
  receiptSteps: {
    margin: "14px 0 10px",
    paddingLeft: 20,
    fontSize: 14,
    lineHeight: 1.5,
    color: "var(--text, #F0E6D4)",
  },
  tillActions: {
    display: "flex",
    gap: 8,
  },
  // minWidth "min-content": with flex: 1 these shrink to fit the row, and global.css's phone
  // min-width (44px) replaces the content floor, so "🔁 Redo Opening Count" ran past its box.
  actionBtnSmall: {
    flex: 1,
    minWidth: "min-content",
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 46,
  },
  goldBtnSmall: {
    flex: 1,
    minWidth: "min-content",
    background: "var(--gold, #F9AF36)",
    border: "none",
    color: "#111",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 46,
  },
  goldOutlineBtnSmall: {
    flex: 1,
    background: "transparent",
    border: "2px solid var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 46,
  },
  tableCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    overflow: "hidden",
  },
  tableResponsiveWrap: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    minWidth: 600,
  },
  th: {
    background: "rgba(0,0,0,0.3)",
    textAlign: "left",
    padding: "10px 14px",
    color: "var(--gold, #F9AF36)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1px solid var(--border, #2A1F14)",
  },
  tr: {
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  td: {
    padding: "10px 14px",
    color: "var(--text, #F0E6D4)",
  },
  badgeOk: {
    color: "var(--green, #27ae60)",
    fontSize: 11,
    fontWeight: 700,
  },
  badgeWarn: {
    color: "var(--gold, #F9AF36)",
    fontSize: 11,
    fontWeight: 700,
  },
  empty: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #8A7A66)",
    background: "var(--surface, #13100B)",
    borderRadius: 8,
    border: "1px solid var(--border, #2A1F14)",
  },

  // Modal
  pastDayNotice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
    margin: "0 0 14px",
    padding: "10px 14px",
    borderRadius: 8,
    background: "rgba(249, 175, 54, 0.10)",
    border: "1px solid rgba(249, 175, 54, 0.35)",
    color: "var(--text)",
    fontSize: 13,
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modalOverlayMobile: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    zIndex: 1000,
    padding: 0,
  },
  modalBody: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 10,
    width: 600,
    maxWidth: "95vw",
    maxHeight: "85vh",
    overflowY: "auto",
    padding: 24,
  },
  modalBodyMobile: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: "16px 16px 0 0",
    width: "100%",
    maxWidth: "100vw",
    maxHeight: "92vh",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "16px 14px 32px",
    boxSizing: "border-box",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    borderBottom: "1px solid var(--border, #2A1F14)",
    paddingBottom: 12,
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  modalMeta: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 20,
    color: "var(--muted, #8A7A66)",
    cursor: "pointer",
    padding: "4px 8px",
    minHeight: 40,
    minWidth: 40,
  },
  denomSection: {
    marginBottom: 16,
    width: "100%",
    boxSizing: "border-box",
  },
  countedTotalRow: {
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 14,
    padding: "12px 14px",
    background: "rgba(19, 16, 11, 0.96)",
    backdropFilter: "blur(10px)",
    borderRadius: 8,
    border: "1px solid rgba(249, 175, 54, 0.4)",
  },
  countedTotalVal: {
    fontSize: 20,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
  },
  splitSection: {
    marginBottom: 16,
    background: "rgba(0,0,0,0.2)",
    padding: 12,
    borderRadius: 8,
    border: "1px solid var(--border, #2A1F14)",
  },
  splitBox: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  splitRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
  },
  fieldLabel: {
    display: "block",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--muted, #8A7A66)",
    marginBottom: 6,
    fontWeight: 700,
  },
  input: {
    width: "100%",
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: "10px 12px",
    fontSize: 16,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
    minHeight: 44,
  },
  saveBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 8,
    padding: "12px 20px",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 48,
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted, #8A7A66)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    minHeight: 48,
  },
  modalActionsMobile: {
    marginTop: 20,
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    gap: 10,
  },
  modalActionsDesktop: {
    marginTop: 20,
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  quickIncRow: {
    display: "flex",
    gap: 6,
    width: "100%",
  },
  quickIncBtn: {
    flex: 1,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    color: "var(--text, #F0E6D4)",
    fontSize: 12,
    fontWeight: 700,
    padding: "6px 0",
    cursor: "pointer",
    textAlign: "center",
    minHeight: 36,
  },
  quickClrBtn: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.3)",
    borderRadius: 6,
    color: "#ff8b7b",
    fontSize: 12,
    fontWeight: 700,
    padding: "6px 12px",
    cursor: "pointer",
    minHeight: 36,
  },
  previewSlipBtn: {
    background: "none",
    border: "1px solid var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 32,
  },
  safeSlipBox: {
    background: "#fff",
    color: "#000",
    padding: "16px",
    borderRadius: 8,
    border: "2px solid #000",
    fontFamily: "Georgia, serif",
  },
  slipHeader: {
    textAlign: "center",
    marginBottom: 12,
    borderBottom: "1px solid #000",
    paddingBottom: 8,
  },
  slipLogo: {
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 2,
  },
  slipSub: {
    fontSize: 10,
    letterSpacing: 1,
    marginTop: 2,
  },
  slipMetaGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 4,
    fontSize: 11,
    marginBottom: 10,
    borderBottom: "1px dashed #666",
    paddingBottom: 8,
  },
  slipTableWrap: {
    marginBottom: 10,
  },
  slipTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 11,
  },
  slipTh: {
    textAlign: "left",
    borderBottom: "1px solid #000",
    padding: "2px 4px",
    fontSize: 10,
  },
  slipTr: {
    borderBottom: "1px dotted #ccc",
  },
  slipTd: {
    padding: "2px 4px",
  },
  slipTotals: {
    borderTop: "1px solid #000",
    paddingTop: 6,
    marginBottom: 16,
  },
  slipTotalRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    marginBottom: 2,
  },
  slipSignatures: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginTop: 20,
  },
  sigBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  sigLine: {
    width: "100%",
    borderBottom: "1px solid #000",
    marginBottom: 4,
    height: 16,
  },
  sigLabel: {
    fontSize: 9,
    textTransform: "uppercase",
    color: "#444",
  },
  tabletGuardContainer: {
    minHeight: "75vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  tabletGuardCard: {
    maxWidth: 440,
    width: "100%",
    background: "var(--surface)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 16,
    padding: "32px 24px",
    textAlign: "center",
    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.4)",
  },
  tabletGuardIcon: {
    fontSize: 40,
    marginBottom: 16,
  },
  tabletGuardTitle: {
    fontSize: 20,
    color: "var(--gold)",
    margin: "0 0 12px",
    fontFamily: "Georgia, serif",
  },
  tabletGuardText: {
    fontSize: 14,
    color: "var(--text)",
    lineHeight: 1.5,
    margin: "0 0 10px",
  },
  tabletGuardSubText: {
    fontSize: 12,
    color: "var(--muted)",
    lineHeight: 1.4,
    margin: "0 0 24px",
  },
  tabletGuardBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: "none",
    borderRadius: 10,
    padding: "12px 24px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  // Morning Change Swap Alert Styles
  morningSwapAlertCard: {
    background: "rgba(249, 175, 54, 0.08)",
    border: "1px solid rgba(249, 175, 54, 0.4)",
    borderRadius: 12,
    padding: "16px 20px",
    marginBottom: 20,
  },
  morningSwapAlertHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 12,
  },
  morningSwapIconBadge: {
    fontSize: 24,
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid rgba(249, 175, 54, 0.4)",
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  morningSwapTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    fontFamily: "Georgia, serif",
    margin: "0 0 4px 0",
  },
  morningSwapDesc: {
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    lineHeight: 1.4,
  },
  morningSwapRegistersList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 10,
    borderTop: "1px solid rgba(249, 175, 54, 0.2)",
    paddingTop: 12,
  },
  morningSwapItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    background: "rgba(0, 0, 0, 0.25)",
    padding: "10px 14px",
    borderRadius: 8,
    border: "1px solid rgba(255, 255, 255, 0.04)",
  },
  morningSwapItemName: {
    fontSize: 14,
    color: "var(--text, #F0E6D4)",
    marginBottom: 3,
  },
  morningSwapItemRec: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
  },
  completeSwapBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: "none",
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  tillSwapBanner: {
    marginTop: 8,
    padding: "8px 10px",
    background: "rgba(249, 175, 54, 0.08)",
    border: "1px solid rgba(249, 175, 54, 0.25)",
    borderRadius: 6,
  },
  tillSwapHeader: {
    fontSize: 11,
    color: "var(--gold, #F9AF36)",
    fontWeight: 700,
    marginBottom: 2,
  },
  tillSwapRec: {
    fontSize: 10,
    color: "var(--muted, #8A7A66)",
    lineHeight: 1.3,
  },
  changeSwapBtnSmall: {
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid var(--gold, #F9AF36)",
    borderRadius: 6,
    color: "var(--gold, #F9AF36)",
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  swapRecommendationCard: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    background: "rgba(249, 175, 54, 0.1)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    borderRadius: 8,
    padding: "12px 14px",
    marginBottom: 16,
  },
  swapRecIcon: {
    fontSize: 20,
  },
  swapRecTitle: {
    fontSize: 13,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    marginBottom: 4,
  },
  swapRecText: {
    fontSize: 12,
    color: "var(--text, #F0E6D4)",
    lineHeight: 1.4,
  },
};
